import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as followupReasonsApi from './followup-reasons.api';

import type {
  CreateFollowupReasonRequest,
  FollowupReasonListQuery,
  UpdateFollowupReasonRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: FollowupReasonListQuery) => ['followup-reasons', 'list', query] as const,
  detail: (id: string) => ['followup-reasons', 'detail', id] as const,
};

export const useFollowupReasonsList = (query: FollowupReasonListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => followupReasonsApi.listFollowupReasons(query),
    staleTime: 30_000,
  });

export const useCreateFollowupReason = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateFollowupReasonRequest) =>
      followupReasonsApi.createFollowupReason(payload),
    onSuccess: (dept) => {
      qc.invalidateQueries({ queryKey: ['followup-reasons', 'list'] });
      toast.success(`Added ${dept.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add followupReason'),
  });
};

export const useUpdateFollowupReason = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateFollowupReasonRequest }) =>
      followupReasonsApi.updateFollowupReason(id, payload),
    onSuccess: (dept) => {
      qc.invalidateQueries({ queryKey: ['followup-reasons', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(dept.id) });
      toast.success('FollowupReason updated');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update followupReason'),
  });
};

export const useDeleteFollowupReason = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => followupReasonsApi.deleteFollowupReason(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['followup-reasons', 'list'] });
      toast.success('FollowupReason deleted');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to delete followupReason'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
